import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { LoginCallback, AuthGuard } from '@vmlmap/shared-ui/auth';

import { AppShell } from '@/layouts/AppShell';
import { AboutPage } from '@/pages/about/AboutPage';
import { MediaPlanApprovalPage } from '@/pages/media-plan-approval/MediaPlanApprovalPage';
import { NotificationsPage } from '@/pages/notifications/NotificationsPage';
import { ProfileView } from '@/views/ProfileView';
import { ContentTrackerPage } from '@/pages/content-tracker/ContentTrackerPage';
import { SuiteHomePage } from '@/pages/suite-home/SuiteHomePage';
import { MockupsPage } from '@/pages/mockups/MockupsPage';
import { UsersPage } from '@/pages/users/UsersPage';
import { AssetPreviewPage } from '@/pages/asset-preview/AssetPreviewPage';
import { AssetLibraryPage } from '@/pages/asset-library/AssetLibraryPage';
import { MyBriefsPage } from '@/pages/asset-library/briefs/MyBriefsPage';
import { parseBriefsSearch } from '@/pages/asset-library/briefs/search';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { RequireAppAccess } from '@/components/RequireAppAccess';
import { ASSET_LIBRARY, CONTENT_TRACKER } from '@/hooks/useAppAccess';
import { isFeatureEnabled } from '@/config/featureFlags';
// import { ChatView } from "@/views/ChatView"; // Chat feature disabled

const rootRoute = createRootRoute({
  component: Outlet,
});

const loginCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'login/callback',
  component: LoginCallback,
});

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_auth',
  component: AuthGuard,
});

const shellRoute = createRoute({
  getParentRoute: () => authRoute,
  id: '_shell',
  component: AppShell,
});

// Pathless layout route carrying the `content_tracker` grant check for every
// page Content Tracker owns. Attached here rather than around each component
// for the same reason the backend attaches its guard at router level: a
// per-page wrapper is one forgotten import away from a hole.
//
// Suite-level routes stay outside it — see the route tree at the bottom.
const contentTrackerRoute = createRoute({
  getParentRoute: () => shellRoute,
  id: '_contentTracker',
  component: () => (
    <RequireAppAccess app={CONTENT_TRACKER}>
      <Outlet />
    </RequireAppAccess>
  ),
});

const campaignTrackerRoute = createRoute({
  getParentRoute: () => contentTrackerRoute,
  path: 'content-tracker',
  component: ContentTrackerPage,
});

// `/` is only a landing redirect: the suite hub where the suite shell is on,
// Content Tracker where it is off. CI builds dev and prod with it on, so this is
// the hub everywhere but a local build that leaves the flag unset. Kept as
// a route rather than dropped so old bookmarks and the bare frontend URL in
// notification emails still land somewhere sensible.
const indexRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: isFeatureEnabled('suiteShell') ? '/suite' : '/content-tracker' });
  },
});

const suiteHomeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: 'suite',
  component: SuiteHomePage,
});

const aboutRoute = createRoute({
  getParentRoute: () => contentTrackerRoute,
  path: 'about',
  component: AboutPage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: 'notifications',
  component: NotificationsPage,
});

const mediaPlanApprovalRoute = createRoute({
  getParentRoute: () => contentTrackerRoute,
  path: 'media-plan-approval',
  component: MediaPlanApprovalPage,
});

const profileRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: 'profile',
  component: ProfileView,
});

const mockupsRoute = createRoute({
  getParentRoute: () => contentTrackerRoute,
  path: 'mockups',
  component: MockupsPage,
});

const userManagementRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: 'admin/users',
  component: UsersPage,
});

// const chatRoute = createRoute({
//   getParentRoute: () => shellRoute,
//   path: "chat",
//   component: ChatView
// }); // Chat feature disabled

const assetPreviewPrototypeRoute = createRoute({
  getParentRoute: () => contentTrackerRoute,
  path: 'asset-preview-prototype',
  component: AssetPreviewPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => contentTrackerRoute,
  path: 'dashboard',
  component: DashboardPage,
});

const campaignPreviewRoute = createRoute({
  getParentRoute: () => contentTrackerRoute,
  path: 'campaigns/$campaignId/preview',
  component: AssetPreviewPage,
});

// Asset Library is a separate backend service, but shares this frontend as one
// product surface. Gated on the same `suiteShell` flag as the shell itself:
// without the suite shell there is no app switcher to reach it from, so the
// route would be unreachable without the suite chrome. Gated again on the
// per-user `asset_library` grant, since the flag says the app exists and the
// grant says this person may open it.
const assetLibraryRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: 'assets',
  component: () => (
    <RequireAppAccess app={ASSET_LIBRARY}>
      <AssetLibraryPage />
    </RequireAppAccess>
  ),
});

// Under `/assets` and behind the same grant, not beside the Content Tracker
// pages: briefs live in Asset Library's backend and `can_brief` is its
// permission, so an Asset-Library-only user must reach it.
const myBriefsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: 'assets/briefs',
  validateSearch: parseBriefsSearch,
  component: () => (
    <RequireAppAccess app={ASSET_LIBRARY}>
      <MyBriefsPage />
    </RequireAppAccess>
  ),
});

const routeTree = rootRoute.addChildren([
  loginCallbackRoute,
  authRoute.addChildren([
    shellRoute.addChildren([
      // Suite-level surfaces: reachable by any provisioned user, whichever app
      // they were granted. Mirrors the backend carve-out — these are served by
      // the ungated users/user routers, because the shell reads `app_access`
      // from `/users/me` to decide what to render. Gating them would lock an
      // Asset-Library-only user out of the hub, the user panel they may
      // administer, and their own profile.
      indexRoute,
      suiteHomeRoute,
      notificationsRoute,
      profileRoute,
      userManagementRoute,
      // Content Tracker's own pages, behind the `content_tracker` grant.
      contentTrackerRoute.addChildren([
        campaignTrackerRoute,
        aboutRoute,
        mediaPlanApprovalRoute,
        mockupsRoute,
        // Dashboard visibility is additionally backend-driven (the
        // `can_view_dashboard` permission drives the nav item and an in-page
        // redirect); the grant here is the coarser, app-level check.
        dashboardRoute,
        assetPreviewPrototypeRoute,
        campaignPreviewRoute,
      ]),
      ...(isFeatureEnabled('suiteShell') ? [assetLibraryRoute, myBriefsRoute] : []),
      // chatRoute // Chat feature disabled
    ]),
  ]),
]);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export default router;
